import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  styled,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DefaultTable from '../../../components/DefaultTable/DefaultTable';
import DetailsCard from '../../../components/DetailsCard/DetailsCard';
import FileDropZone from '../../../components/FileDropZone/FileDropZone';
import FormatCurrency from '../../../components/FormatNumber/FormatCurrency';
import FormatPhone from '../../../components/FormatNumber/FormatPhone';
import PageTitle from '../../../components/PageTitle/PageTitle';
import TypographyDash from '../../../components/TypographyDash/TypographyDash';
import { dashBorder, textEllipsis } from '../../../constants';
import { PaymentStatus } from '../../../enums/paymentStatus';
import { PaymentType } from '../../../enums/paymentType';
import { TalentStatus } from '../../../enums/talentStatus';
import { TalentType } from '../../../enums/talentType';
import { useTableFunctions } from '../../../hooks/useTableFunctions';
import { useUserRoles } from '../../../hooks/useUserRoles';
import { FileType } from '../../../models/fileType';
import { HeadCell } from '../../../models/headCell';
import { Pagination } from '../../../models/pagination';
import { Payment } from '../../../models/payment';
import { Talent } from '../../../models/talent';
import { CREATE_PAYMENT } from '../../../router/constants';
import { fileService } from '../../../services/fileService';
import { paymentService } from '../../../services/paymentService';
import { talentService } from '../../../services/talentService';
import { getRoundStatusStyle } from '../../../utils/statusRoundStyle';
import PaymentInformationForm from './PaymentInformationForm';

const BoldText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.grey['900']
}));

const initialPaymentsColumnData: HeadCell[] = [
  {
    id: 'project.projectTitle',
    label: 'Project',
    sortable: true,
    visible: true,
    render: (payment: Payment) =>
      payment.project ? payment.project.projectTitle : '--'
  },
  {
    id: 'invoice.invoiceNumber',
    label: 'Invoice #',
    sortable: true,
    visible: true,
    render: (payment: Payment) =>
      payment.invoice ? payment.invoice.invoiceNumber : '--'
  },
  {
    id: 'category',
    label: 'Category',
    sortable: true,
    visible: true,
    render: (payment: Payment) => payment.category
  },
  {
    id: 'location.paymentType',
    label: 'Payment Type',
    sortable: true,
    visible: true,
    render: (payment: Payment) => PaymentType[payment.paymentType]
  },
  {
    id: 'description',
    label: 'Description',
    sortable: true,
    visible: true,
    render: (payment: Payment) => (
      <Box sx={{ ...textEllipsis, maxWidth: 200 }}>{payment.description}</Box>
    )
  },

  {
    id: 'amount',
    label: 'Amount',
    sortable: true,
    visible: true,
    render: (payment: Payment) => <FormatCurrency value={payment.totalAmount} />
  },
  {
    id: 'paymentStatus',
    label: 'Status',
    sortable: true,
    visible: true,
    thStyle: { width: 170, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (payment: Payment) => (
      <Box
        sx={{
          ...getRoundStatusStyle(PaymentStatus[payment.paymentStatus])
        }}
      >
        {PaymentStatus[payment.paymentStatus]}
      </Box>
    )
  }
];

const TalentDetails = (): JSX.Element => {
  const navigate = useNavigate();
  const [talent, setTalent] = useState<Talent | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const { id } = useParams();
  const { isAdmin } = useUserRoles();
  const [paginationPayments, setPaginationPayments] = useState<
    Pagination<Payment> | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const {
    columnsData,
    page,
    rowsPerPage,
    sortBy,
    sortType,
    onRequestSort,
    onChangePage,
    onChangeRowsPerPage
  } = useTableFunctions(initialPaymentsColumnData, undefined);

  useEffect(() => {
    getTalent();
  }, [id]);

  useEffect(() => {
    getPayments();
  }, [id, page, rowsPerPage, sortBy, sortType]);

  const getTalent = () => {
    talentService.getTalentById(Number(id)).then((response) => {
      setTalent(response.data);
    });
  };

  const getPayments = () => {
    paymentService.getPayments({ talents: [Number(id)] }).then((response) => {
      setPaginationPayments(response.data);
      setLoading(false);
    });
  };

  const addAttachments = (files: File[]) => {
    if (talent?.id) {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      talentService
        .addTalentAttachment(talent?.id, formData)
        .then((response) => {
          setTalent((prevState) => {
            if (prevState) {
              return {
                ...prevState,
                attachments: [...prevState.attachments, ...response.data]
              };
            }
          });
        });
    }
  };

  const onDeleteAttachment = (fileToRemove: FileType) => {
    fileService.deleteFileById(fileToRemove.uuid).then(() => {
      const files = talent?.attachments.filter(
        (item: FileType) => item !== fileToRemove
      );
      setTalent((prevState) => {
        if (prevState && files) {
          return {
            ...prevState,
            attachments: [...files]
          };
        }
      });
    });
  };

  const onEditAttachment = (file: FileType) => {
    fileService
      .editFileName({ uuid: file.uuid, fileName: file.fileName })
      .then(() => {
        setTalent((prevState) => {
          if (prevState) {
            return {
              ...prevState,
              attachments: prevState.attachments.map((attachment) =>
                attachment.uuid === file.uuid
                  ? { ...attachment, fileName: file.fileName }
                  : attachment
              )
            };
          }
        });
      });
  };

  return (
    <>
      <PageTitle>Talent Profile</PageTitle>
      <DetailsCard
        title={
          talent?.firstName && talent?.lastName
            ? ` ${talent?.firstName} ${talent?.lastName} #${talent?.id}`
            : ''
        }
        status={talent?.status ? TalentStatus[talent.status] : undefined}
      >
        <Grid gap={5} container>
          <Grid item xs sx={{ ...dashBorder }}>
            <Typography variant="h6" color="grey.900" mb={2}>
              Contact Information
            </Typography>
            <Box display="flex" gap={3}>
              <Stack spacing={1} sx={{ flexShrink: 0 }}>
                <BoldText>First Name:</BoldText>
                <BoldText>Last Name:</BoldText>
                <BoldText>Address:</BoldText>
                <BoldText>City:</BoldText>
                <BoldText>State:</BoldText>
                <BoldText>Zip Code:</BoldText>
                <BoldText>Email:</BoldText>
                <BoldText>Phone:</BoldText>
              </Stack>
              <Stack spacing={1} sx={{ flex: '1 0 160px' }}>
                <TypographyDash value={talent?.firstName} />
                <TypographyDash value={talent?.lastName} />
                <TypographyDash value={talent?.address} />
                <TypographyDash value={talent?.city} />
                <TypographyDash value={talent?.state} />
                <TypographyDash value={talent?.zipCode} />
                <TypographyDash value={talent?.email} />
                <Typography>
                  {talent?.phoneNumber ? (
                    <FormatPhone value={talent?.phoneNumber} />
                  ) : (
                    '--'
                  )}
                </Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item xs sx={{ ...dashBorder }}>
            {!isEditing && (
              <>
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Typography variant="h6" color="grey.900">
                    Payment Information
                  </Typography>
                  <IconButton onClick={() => setIsEditing(true)}>
                    <EditIcon sx={{ cursor: 'pointer' }} />
                  </IconButton>
                </Box>
                <Box display="flex" gap={3}>
                  <Stack spacing={1} sx={{ flexShrink: 0 }}>
                    <BoldText>Type:</BoldText>
                    <BoldText>Department:</BoldText>
                    <BoldText>Category:</BoldText>
                    <BoldText>GL/Accounting Code:</BoldText>
                    {isAdmin && <BoldText>Payment Method:</BoldText>}
                  </Stack>
                  <Stack spacing={1} sx={{ flex: '1 0 160px' }}>
                    <Typography>
                      {talent?.accountType
                        ? TalentType[talent?.accountType]
                        : '--'}
                    </Typography>
                    <TypographyDash value={talent?.department} />
                    <TypographyDash value={talent?.category} />
                    <TypographyDash value={talent?.glAccountingCode} />
                    {isAdmin && (
                      <TypographyDash value={talent?.paymentMethod} />
                    )}
                  </Stack>
                </Box>
              </>
            )}
            {isEditing && (
              <PaymentInformationForm
                setIsEditing={setIsEditing}
                talent={talent ?? undefined}
                reloadInfo={() => getTalent()}
              />
            )}
          </Grid>
        </Grid>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 3
          }}
        >
          <Typography sx={{ mt: 0.5 }} variant="h5" color="grey.900">
            Payment History
          </Typography>
          <Button variant="lightBlue" onClick={() => navigate(CREATE_PAYMENT)}>
            + SEND PAYMENT
          </Button>
        </Box>
        <DefaultTable
          columns={columnsData}
          data={paginationPayments?.content}
          paginationProps={{
            page,
            rowsPerPage,
            onChangePage,
            onChangeRowsPerPage,
            totalElements: paginationPayments?.totalElements
          }}
          sortProps={{ sortBy, sortType, onRequestSort }}
        />
        {!loading && !paginationPayments?.content.length && (
          <Typography variant="h5" align="center" m={4}>
            No Payments found!
          </Typography>
        )}
        <Box my={4}>
          <FileDropZone
            attachments={talent?.attachments}
            onSaveFiles={addAttachments}
            onDeleteAttachment={onDeleteAttachment}
            onEditAttachment={onEditAttachment}
          />
        </Box>
      </DetailsCard>
    </>
  );
};

export default TalentDetails;
